import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../../constants/emailjs.constants';
import { User } from '../../types';
import {
  buildEmailTemplate,
  buildNewAdminMessage,
  buildWelcomeAdminMessage,
  buildNewBookMessage,
} from './emailTemplate';

// Initialize EmailJS
emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);

interface NewAdminNotificationData {
  newAdmin: {
    firstName: string;
    lastName: string;
    email: string;
    title?: string;
  };
  addedBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface NewBookNotificationData {
  book: {
    title: string;
    author: string;
    isbn: string;
    category?: string;
  };
  addedBy: {
    firstName: string;
    lastName: string;
  };
}

interface WelcomeAdminData {
  admin: {
    firstName: string;
    lastName: string;
    email: string;
    title?: string;
  };
  tempPassword: string;
  addedBy: {
    firstName: string;
    lastName: string;
  };
}

export const emailNotificationService = {
  /**
   * Notify all admins when a new admin is added
   */
  async notifyAdminsOfNewAdmin(
    admins: User[],
    data: NewAdminNotificationData
  ): Promise<void> {
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const messageHtml = buildNewAdminMessage({
      newAdminName: `${data.newAdmin.firstName} ${data.newAdmin.lastName}`,
      newAdminEmail: data.newAdmin.email,
      newAdminTitle: data.newAdmin.title || 'Not specified',
      addedByName: `${data.addedBy.firstName} ${data.addedBy.lastName}`,
      addedByEmail: data.addedBy.email,
      date,
    });

    const promises = admins.map((admin) => {
      const fullHtml = buildEmailTemplate({
        recipientName: `${admin.firstName} ${admin.lastName}`,
        subject: 'New Administrator Added',
        message: messageHtml,
      });

      return emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATES.NEW_ADMIN_NOTIFICATION,
        {
          to_email: admin.email,
          to_name: `${admin.firstName} ${admin.lastName}`,
          subject: 'New Administrator Added',
          message: fullHtml,
        }
      );
    });

    try {
      await Promise.allSettled(promises);
      console.log('Admin notifications sent successfully');
    } catch (error) {
      console.error('Error sending admin notifications:', error);
    }
  },

  /**
   * Send welcome email to newly added admin with their credentials
   */
  async sendWelcomeEmailToAdmin(data: WelcomeAdminData): Promise<void> {
    try {
      const date = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const messageHtml = buildWelcomeAdminMessage({
        adminTitle: data.admin.title || 'Administrator',
        tempPassword: data.tempPassword,
        addedByName: `${data.addedBy.firstName} ${data.addedBy.lastName}`,
        loginUrl: window.location.origin,
        date,
      });

      const fullHtml = buildEmailTemplate({
        recipientName: `${data.admin.firstName} ${data.admin.lastName}`,
        subject: 'Welcome to GHAFES Library - Admin Access',
        message: messageHtml,
      });

      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATES.WELCOME_ADMIN,
        {
          to_email: data.admin.email,
          to_name: `${data.admin.firstName} ${data.admin.lastName}`,
          subject: 'Welcome to GHAFES Library - Admin Access',
          message: fullHtml,
        }
      );
      console.log('Welcome email sent successfully to new admin');
    } catch (error) {
      console.error('Error sending welcome email:', error);
    }
  },

  /**
   * Notify all admins when a new book is added
   */
  async notifyAdminsOfNewBook(
    admins: User[],
    data: NewBookNotificationData
  ): Promise<void> {
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const messageHtml = buildNewBookMessage({
      bookTitle: data.book.title,
      bookAuthor: data.book.author,
      bookIsbn: data.book.isbn,
      bookCategory: data.book.category || 'Uncategorized',
      addedByName: `${data.addedBy.firstName} ${data.addedBy.lastName}`,
      date,
    });

    const promises = admins.map((admin) => {
      const fullHtml = buildEmailTemplate({
        recipientName: `${admin.firstName} ${admin.lastName}`,
        subject: 'New Book Added to Library',
        message: messageHtml,
      });

      return emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATES.NEW_BOOK_NOTIFICATION,
        {
          to_email: admin.email,
          to_name: `${admin.firstName} ${admin.lastName}`,
          subject: 'New Book Added to Library',
          message: fullHtml,
        }
      );
    });

    try {
      await Promise.allSettled(promises);
      console.log('Book notification emails sent successfully');
    } catch (error) {
      console.error('Error sending book notifications:', error);
    }
  },
};
